import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CONFIGURATION_PRODUCT_OPTION_ID, CYLINDO_OPTIONS, FABRIC_PRODUCT_OPTION_ID, LEATHER_PRODUCT_OPTION_ID } from 'commons/constants';
import { orientationOptionIds } from 'components/consumer/OrientationPDPModal';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useCylindoDimensionShots from '../useCylindoDimensionShots';
import useCylindoCache from './cylindoCache/useCylindoCache';
import resolveCylindoUpholsteryCode from './resolveCylindoUpholsteryCode';
import resolveCylindoPowerOptionsCode from './resolveCylindoPowerOptionsCode';
import buildCylindoUrl from './cylindoCache/buildCylindoUrl';
import { DISABLED_UPHOLSTERY, EMPTY_ARRAY, EMPTY_OBJECT, CYLINDO_FRAMES_TO_SHOW } from './cylindoCache/defaults';

// CON-7441: Open/Close is a view mode (not a purchase option), so it toggles the live
// viewer via cylindoFeatures. Power (2/3 recliner) is a backend option (170), driven
// from the "Select Configuration" dropdown, not here.
//
// MECHANICAL_OPEN / MECHANICAL_CLOSED are internal view-mode tokens. Cylindo authors the
// mechanism under two feature codes — legacy CYLINDO_MECHANICAL (option CLOSE) and newer
// RECLINING OPTIONS (option CLOSED) — so each token maps to the set of live option codes
// that mean it, and we match the feature by either code.
const MECHANICAL_OPEN = 'OPEN';
const MECHANICAL_CLOSED = 'CLOSE';
const MECHANICAL_FEATURE_CODES = [CYLINDO_OPTIONS.MECHANICAL, CYLINDO_OPTIONS.RECLINING_OPTIONS];
const OPTION_CODES_BY_STATE = {
  [MECHANICAL_OPEN]: ['OPEN'],
  [MECHANICAL_CLOSED]: ['CLOSE', 'CLOSED']
};
const usePDPCylindoViewer = t0 => {
  const $ = _c(52);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isPDPDesktop: t2,
    material: t3,
    deferCylindoFetch: t4
  } = t1;
  const isPDPDesktop = t2 === undefined ? false : t2;
  const material = t3 === undefined ? null : t3;
  const deferCylindoFetch = t4 === undefined ? false : t4;
  const {
    data: basicDetails
  } = usePDPBasicDetails();
  const {
    data: t5
  } = usePDPOptions();
  const {
    selectedOptionsExtended
  } = t5;
  const productId = basicDetails?.id;
  const isCylindoProduct = !!basicDetails?.custom_properties?.cylindo_product;
  const [openCloseState, setOpenCloseState] = useState(MECHANICAL_OPEN);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setOpenCloseState(MECHANICAL_OPEN);
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== productId) {
    t7 = [productId];
    $[3] = productId;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  useEffect(t6, t7);
  const {
    config: cylindoProductConfig,
    version: cylindoVersion,
    loading
  } = useCylindoCache(productId, isCylindoProduct, deferCylindoFetch);
  material?.label;
  let t8;
  bb0: {
    if (!productId || !cylindoProductConfig || !selectedOptionsExtended) {
      t8 = null;
      break bb0;
    }
    const fabricOption = selectedOptionsExtended[FABRIC_PRODUCT_OPTION_ID]?.value;
    const leatherOption = selectedOptionsExtended[LEATHER_PRODUCT_OPTION_ID]?.value;
    const selectedOption = fabricOption || leatherOption || null;
    const jbName = material?.label ?? selectedOption?.value ?? null;
    const jbSku = selectedOption?.sku ?? null;
    if (!jbName) {
      t8 = null;
      break bb0;
    }
    let resolved;
    let t9;
    if ($[5] !== cylindoProductConfig.features || $[6] !== jbName || $[7] !== jbSku) {
      const upholsteryFeature = cylindoProductConfig.features?.find(_temp);
      resolved = resolveCylindoUpholsteryCode({
        jbSku,
        jbName,
        cylindoOptions: upholsteryFeature?.options
      });
      t9 = resolved && DISABLED_UPHOLSTERY.includes(resolved);
      $[5] = cylindoProductConfig.features;
      $[6] = jbName;
      $[7] = jbSku;
      $[8] = resolved;
      $[9] = t9;
    } else {
      resolved = $[8];
      t9 = $[9];
    }
    if (t9) {
      t8 = null;
      break bb0;
    }
    t8 = resolved;
  }
  const upholsteryKey = t8;
  const hasUpholsteryOption = !!upholsteryKey;
  let t9;
  if ($[10] !== cylindoVersion || $[11] !== upholsteryKey) {
    t9 = {
      upholsteryKey,
      cylindoVersion
    };
    $[10] = cylindoVersion;
    $[11] = upholsteryKey;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const dimensionShotsData = useCylindoDimensionShots(t9);
  let t10;
  bb1: {
    if (!upholsteryKey) {
      t10 = undefined;
      break bb1;
    }
    let t11;
    if ($[13] !== upholsteryKey) {
      t11 = {
        [CYLINDO_OPTIONS.UPHOLSTERY]: upholsteryKey
      };
      $[13] = upholsteryKey;
      $[14] = t11;
    } else {
      t11 = $[14];
    }
    t10 = t11;
  }
  const upholsteryFeatureDetails = t10;
  const isUpholsteryFeatureAvailable = !!upholsteryFeatureDetails;
  const orientationId = orientationOptionIds.find(id => selectedOptionsExtended[id]);
  const orientationValue = selectedOptionsExtended[orientationId]?.value?.value;
  let t11;
  if ($[15] !== orientationValue) {
    t11 = orientationValue?.toUpperCase();
    $[15] = orientationValue;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const orientationKey = t11;
  const hasOrientationOption = !!orientationKey;
  let t12;
  if ($[17] !== cylindoProductConfig?.features || $[18] !== orientationKey) {
    t12 = () => {
      if (!orientationKey) {
        return;
      }
      const orientationFeature = cylindoProductConfig?.features?.find(_temp2);
      const orientationKeyStr = String(orientationKey).trim().toLowerCase();
      const orientationOption = orientationFeature?.options?.find(opt => String(opt.code).trim().toLowerCase() === orientationKeyStr);
      if (!orientationOption) {
        return;
      }
      return {
        [CYLINDO_OPTIONS.ORIENTATION]: orientationOption.code
      };
    };
    $[17] = cylindoProductConfig?.features;
    $[18] = orientationKey;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  cylindoProductConfig?.features;
  let t13;
  if ($[20] !== t12) {
    t13 = t12();
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const orientationOptionDetails = t13;
  const isOrientationFeatureAvailable = !!orientationOptionDetails;
  cylindoProductConfig?.features;
  let t14;
  bb2: {
    const selected = selectedOptionsExtended?.[CONFIGURATION_PRODUCT_OPTION_ID]?.value;
    if (!selected) {
      t14 = undefined;
      break bb2;
    }
    let t15;
    if ($[22] !== cylindoProductConfig?.features || $[23] !== selected.sku || $[24] !== selected.value) {
      const powerFeature = cylindoProductConfig?.features?.find(_temp3);
      t15 = resolveCylindoPowerOptionsCode({
        jbSku: selected.sku,
        jbName: selected.value,
        cylindoOptions: powerFeature?.options
      });
      $[22] = cylindoProductConfig?.features;
      $[23] = selected.sku;
      $[24] = selected.value;
      $[25] = t15;
    } else {
      t15 = $[25];
    }
    const code = t15;
    let t16;
    if ($[26] !== code) {
      t16 = code ? {
        [CYLINDO_OPTIONS.POWER_OPTIONS]: code
      } : undefined;
      $[26] = code;
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    t14 = t16;
  }
  const powerOptionDetails = t14;
  const powerCode = powerOptionDetails?.[CYLINDO_OPTIONS.POWER_OPTIONS] ?? null;
  const prevPowerCodeRef = useRef(powerCode);
  let t15;
  let t16;
  if ($[28] !== powerCode) {
    t15 = () => {
      if (powerCode && powerCode !== prevPowerCodeRef.current) {
        setOpenCloseState(MECHANICAL_OPEN);
      }
      prevPowerCodeRef.current = powerCode;
    };
    t16 = [powerCode];
    $[28] = powerCode;
    $[29] = t15;
    $[30] = t16;
  } else {
    t15 = $[29];
    t16 = $[30];
  }
  useEffect(t15, t16);
  cylindoProductConfig?.features;
  let t17;
  if ($[31] !== cylindoProductConfig?.features || $[32] !== openCloseState) {
    bb3: {
      const mechanicalFeature = cylindoProductConfig?.features?.find(_temp4);
      if (!mechanicalFeature?.options?.length) {
        t17 = undefined;
        break bb3;
      }
      const wantedCodes = OPTION_CODES_BY_STATE[openCloseState] || [];
      const match = mechanicalFeature.options.find(opt_0 => wantedCodes.includes(String(opt_0.code).trim().toUpperCase()));
      t17 = match ? {
        [mechanicalFeature.code]: match.code
      } : undefined;
    }
    $[31] = cylindoProductConfig?.features;
    $[32] = openCloseState;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const openCloseDetails = t17;
  const hasOpenCloseFeature = !!openCloseDetails;
  const areAllFeaturesAvailable = (!hasUpholsteryOption || hasUpholsteryOption && isUpholsteryFeatureAvailable) && (!hasOrientationOption || hasOrientationOption && isOrientationFeatureAvailable);
  const t18 = upholsteryFeatureDetails || EMPTY_OBJECT;
  const t19 = orientationOptionDetails || EMPTY_OBJECT;
  const t20 = powerOptionDetails || EMPTY_OBJECT;
  const t21 = openCloseDetails || EMPTY_OBJECT;
  let t22;
  if ($[34] !== t18 || $[35] !== t19 || $[36] !== t20 || $[37] !== t21) {
    t22 = {
      ...t18,
      ...t19,
      ...t20,
      ...t21
    };
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  const cylindoFeatures = t22;
  let t23;
  bb4: {
    if (isPDPDesktop && areAllFeaturesAvailable && Object.entries(cylindoFeatures).length) {
      let t24;
      if ($[39] !== cylindoFeatures || $[40] !== productId) {
        t24 = CYLINDO_FRAMES_TO_SHOW.map((frame, index) => ({
          id: frame,
          index,
          src: buildCylindoUrl({
            productId,
            kind: "frames",
            frame,
            size: 128,
            features: cylindoFeatures
          }),
          type: "cylindo"
        }));
        $[39] = cylindoFeatures;
        $[40] = productId;
        $[41] = t24;
      } else {
        t24 = $[41];
      }
      t23 = t24;
      break bb4;
    }
    t23 = EMPTY_ARRAY;
  }
  const cylindoConfigurationPhotos = t23;
  const isMechanicalOpen = isPDPDesktop && hasOpenCloseFeature ? openCloseState === MECHANICAL_OPEN : null;
  let t24;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setOpenCloseState(_temp5);
    };
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const toggleMechanicalOpen = t24;
  const t25 = !!cylindoProductConfig && areAllFeaturesAvailable;
  const t26 = isCylindoProduct && !!cylindoProductConfig && areAllFeaturesAvailable && cylindoConfigurationPhotos?.length > 0;
  let t27;
  if ($[43] !== cylindoConfigurationPhotos || $[44] !== cylindoFeatures || $[45] !== dimensionShotsData || $[46] !== isMechanicalOpen || $[47] !== loading || $[48] !== t25 || $[49] !== t26 || $[50] !== upholsteryKey) {
    t27 = {
      cylindoConfigurationPhotos,
      cylindoFeatures,
      isMechanicalOpen,
      toggleMechanicalOpen,
      isCylindoAvailable: t25,
      loading,
      isCylindoConfigurationPhotos: t26,
      upholsteryKey,
      ...dimensionShotsData
    };
    $[43] = cylindoConfigurationPhotos;
    $[44] = cylindoFeatures;
    $[45] = dimensionShotsData;
    $[46] = isMechanicalOpen;
    $[47] = loading;
    $[48] = t25;
    $[49] = t26;
    $[50] = upholsteryKey;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  return t27;
};
export default usePDPCylindoViewer;
function _temp(feat) {
  return feat.code === CYLINDO_OPTIONS.UPHOLSTERY;
}
function _temp2(feat_0) {
  return feat_0.code === CYLINDO_OPTIONS.ORIENTATION;
}
function _temp3(feat_1) {
  return feat_1.code === CYLINDO_OPTIONS.POWER_OPTIONS;
}
function _temp4(feat_2) {
  return MECHANICAL_FEATURE_CODES.includes(feat_2.code);
}
function _temp5(prev) {
  return prev === MECHANICAL_OPEN ? MECHANICAL_CLOSED : MECHANICAL_OPEN;
}